import React, { useEffect, useRef } from 'react';

const BentoItem = ({ className, children }) => {
  const itemRef = useRef(null);

  useEffect(() => {
    const item = itemRef.current;
    if (!item) return;

    const handleMouseMove = (e) => {
      const rect = item.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      item.style.setProperty('--mouse-x', `${x}px`);
      item.style.setProperty('--mouse-y', `${y}px`);
    };

    item.addEventListener('mousemove', handleMouseMove);
    return () => item.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div ref={itemRef} className={`bento-item ${className}`}>
      {children}
    </div>
  );
};

const PricingSection = () => {
  const benefits = [
    {
      title: 'Custom Billing Cycles',
      desc: 'Select weekly, monthly, or annual billing based on your company\'s preference.',
      icon: 'calendar_month',
      size: 'col-span-1 row-span-1'
    },
    {
      title: 'Pay-As-You-Go or Fixed Plans',
      desc: 'Opt for flexible ride-based pricing or lock in fixed rates for predictable expenses.',
      icon: 'payments',
      size: 'col-span-1 row-span-1'
    },
    {
      title: 'Volume-Based Discounts',
      desc: 'The more you ride, the more you save with exclusive corporate discounts.',
      icon: 'trending_down',
      size: 'col-span-1 row-span-1'
    },
    {
      title: 'Dedicated Account Management',
      desc: 'A single point of contact to handle all your travel needs and support.',
      icon: 'support_agent',
      size: 'col-span-1 row-span-1'
    },
    {
      title: 'Priority Booking & Dispatch',
      desc: 'Ensure your team gets rides when they need them—without delays.',
      icon: 'bolt',
      size: 'col-span-1 row-span-1'
    },
    {
      title: 'Advanced Billing Dashboard',
      desc: 'Track usage, manage invoices, and monitor expenses with ease.',
      icon: 'dashboard',
      size: 'col-span-1 row-span-1'
    },
    {
      title: 'Multi-City Operations',
      desc: 'Seamless service across multiple locations for businesses with wider reach.',
      icon: 'public',
      size: 'col-span-2 row-span-1'
    },
  ];

  return (
    <section className="pt-[96px] pb-[96px] bg-surface" id="pricing">
      <div className="max-w-[1400px] mx-auto px-[32px]">
        {/* Header */}
        <div className="text-center mb-[64px] w-full flex flex-col items-center">
          <h2 className="font-headline text-[36px] md:text-[48px] font-extrabold tracking-tight text-on-surface mb-[24px]">
            Tailored Pricing for Every Business
          </h2>
          <p className="text-on-surface-variant text-[18px] max-w-[768px] mx-auto text-center leading-relaxed">
            We understand that every organization has unique travel needs. That's why Pulpit Cabs offers fully customizable pricing plans designed to scale with your business—whether you're a startup, a growing team, or a large enterprise.
          </p>
        </div>

        {/* Cybernetic Bento Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-[32px] mb-[120px]">
          {/* Main Custom Pricing Focus Item */}
          <BentoItem className="col-span-1 sm:col-span-2 row-span-2 p-[48px] flex flex-col justify-between bg-white border-outline-variant/30">
            <div>
              <div className="inline-flex items-center gap-[8px] px-[12px] py-[4px] rounded-full bg-primary/10 text-primary text-[12px] font-bold uppercase tracking-widest mb-[16px]">
                <span className="material-symbols-outlined text-[14px]">tune</span>
                Customizable
              </div>
              <h3 className="font-headline text-[30px] font-bold text-on-surface mb-[16px]">
                Flexible Corporate Pricing
              </h3>
              <p className="text-on-surface-variant text-[18px] leading-relaxed">
                Build a pricing model that works for you. Choose from monthly, weekly, or yearly plans, or create a hybrid structure based on your usage patterns. Our goal is simple: give you maximum value with complete control over your transportation budget.
              </p>
            </div>
            <div className="mt-[48px] p-[32px] bg-primary/5 rounded-[16px] border border-primary/10">
              <p className="text-primary font-medium italic text-[14px]">
                "Designed to grow with you. Whether you need daily employee transport, client pickups, or event-based travel solutions, our pricing adapts as your requirements evolve."
              </p>
            </div>
          </BentoItem>

          {/* Features distributed in Bento style */}
          {benefits.map((benefit, i) => (
            <BentoItem key={i} className={`${benefit.size} p-[32px] flex flex-col justify-center`}>
              <span className="material-symbols-outlined text-primary text-[30px] mb-[12px] block">
                {benefit.icon}
              </span>
              <h4 className="font-bold text-on-surface mb-[8px] text-[14px]">{benefit.title}</h4>
              <p className="text-on-surface-variant text-[12px] leading-relaxed">
                {benefit.desc}
              </p>
            </BentoItem>
          ))}
        </div>

        {/* Final CTA */}
        <div className="relative group max-w-[896px] mx-auto text-center flex flex-col items-center mb-[16px]">
          <div className="absolute -inset-[4px] bg-gradient-to-r from-primary to-secondary rounded-[24px] blur opacity-20 group-hover:opacity-40 transition duration-1000"></div>
          <div className="relative w-full p-[48px] rounded-[24px] bg-white border border-outline-variant/30 shadow-xl flex flex-col items-center">
            <h3 className="font-headline text-[36px] font-bold text-on-surface mb-[16px]">
              Let's Build Your Plan
            </h3>
            <p className="text-on-surface-variant max-w-[600px] text-center text-[18px] leading-relaxed">
              Partner with Pulpit Cabs and create a corporate pricing model that fits your business perfectly.
            </p>
            <div className="mt-[32px] flex justify-center w-full">
              <a href="#contact" className="fancy-dark">
                <span className="top-key"></span>
                <span className="bottom-key-1"></span>
                <span className="bottom-key-2"></span>
                <span className="text">Request Customized Quote</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;


